<template>
  <div class="multi-image-uploader">
    <!-- Drag and Drop Box -->
    <div
      ref="dragDropBox"
      class="image-uploader flex-center-vertically text-center"
      style="min-height: 150px; height: auto; border: 1px dashed"
      :style="{
        borderColor: activeColor,
        cursor: readonly ? '' : 'pointer',
        backgroundColor: readonly ? secondary : '',
        width: fullWidth ? '' : `${pw}px`,
        height: fullWidth ? '' : `${ph}px`,
      }"
      @click="chooseImage"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <!-- Show icon & hint if no files -->
      <div>
        <feather-icon
          icon="ImageIcon"
          size="30"
          :style="{ color: readonly ? '#ffffff' : activeColor }"
        />
        <div
          class="mt-2"
          :style="{ color: readonly ? '#ffffff' : activeColor }"
        >
          {{ $t("general.clickOrDragDropToUpload") }}
        </div>
      </div>
    </div>

    <!-- Preview Container -->
    <div
      v-if="fileList.length"
      class="preview-container"
    >
      <div
        v-for="(fileItem, index) in visibleFiles"
        :key="index"
        class="preview-item"
        @click="openModal(index)"
      >
        <img
          class="img-fluid"
          :src="fileItem.preview"
          alt=""
          style="object-fit: contain;"
        >
        <button
          class="remove-button"
          @click.prevent.stop="remove(index)"
        >
          <CloseIcon
            :width="20"
            :height="20"
          />
        </button>
      </div>
      <div
        v-if="remainingFilesCount > 0"
        class="preview-item remaining-count"
      >
        +{{ remainingFilesCount }}
      </div>
    </div>

    <!-- Hidden File Input -->
    <input
      id="assetsFieldHandle"
      ref="fileInput"
      type="file"
      style="display: none"
      multiple
      name="fields[assetsFieldHandle][]"
      class="w-px h-px opacity-0 overflow-hidden absolute"
      accept=".pdf,.jpg,.jpeg,.png"
      @change="onChange"
    >

    <!-- Image Modal -->
    <b-modal
      v-model="isModalVisible"
      size="lg"
      hide-footer
      class="image-modal"
      @hidden="resetModal"
    >
      <div class="modal-content">
        <b-button
          variant="secondary"
          class="prev-button"
          :disabled="currentImageIndex === 0"
          @click="prevImage"
        >
          <feather-icon
            size="30"
            icon="ChevronLeftIcon"
          />
        </b-button>
        <img
          :src="currentImage.preview"
          class="img-fluid preview-one"
          alt="Full Image"
        >
        <b-button
          variant="secondary"
          class="next-button"
          :disabled="currentImageIndex === fileList.length - 1"
          @click="nextImage"
        >
          <feather-icon
            size="30"
            icon="ChevronRightIcon"
          />
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import CloseIcon from '@/components/icons/CloseIcon.vue'
import { $themeConfig } from '@themeConfig'
// Import feather-icon if needed or ensure it's globally available
import { BButton } from 'bootstrap-vue'

export default {
  delimiters: ['${', '}'],
  components: {
    CloseIcon,
    BButton,
  },

  props: {
    value: {
      // Array of File objects or similar
      type: Array,
      default: () => [],
    },
    pw: {
      type: Number,
      default: 400,
    },
    ph: {
      type: Number,
      default: 300,
    },
    fullWidth: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { primary, secondary } = $themeConfig.color
    const activeColor = secondary
    return { primary, secondary, activeColor }
  },

  data() {
    return {
      fileList: [], // Will store { file: File, preview: string } objects
      isModalVisible: false,
      currentImageIndex: 0,
    }
  },

  computed: {
    visibleFiles() {
      return this.fileList.slice(0, 3)
    },
    remainingFilesCount() {
      return this.fileList.length > 3 ? this.fileList.length - 3 : 0
    },
    currentImage() {
      return this.fileList[this.currentImageIndex] || {}
    },
  },

  methods: {
    onChange() {
      if (this.readonly) return

      const selectedFiles = [...this.$refs.fileInput.files]
      selectedFiles.forEach(file => {
        const reader = new FileReader()
        reader.onload = e => {
          this.fileList.push({
            file,
            preview: e.target.result,
          })
          // Emit the updated file array
          this.emitFiles()
        }
        reader.readAsDataURL(file)
      })
    },
    remove(index) {
      this.fileList.splice(index, 1)
      this.emitFiles()
    },
    emitFiles() {
      // Emit the plain File objects array
      this.$emit('input', this.fileList.map(item => item.file))
      this.$emit('change', this.fileList.map(item => item.file))
    },
    dragover(event) {
      if (this.readonly) return
      event.preventDefault()
      this.activeColor = this.primary
    },
    dragleave(event) {
      if (this.readonly) return
      this.activeColor = this.secondary
    },
    drop(event) {
      if (this.readonly) return
      event.preventDefault()
      this.$refs.fileInput.files = event.dataTransfer.files
      this.onChange() // Trigger the onChange to handle newly dropped files
    },
    chooseImage() {
      if (this.readonly) return
      this.$refs.fileInput.value = null
      this.$refs.fileInput.click()
    },
    openModal(index) {
      this.currentImageIndex = index
      this.isModalVisible = true
    },
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex -= 1
      }
    },
    nextImage() {
      if (this.currentImageIndex < this.fileList.length - 1) {
        this.currentImageIndex += 1
      }
    },
    resetModal() {
      this.isModalVisible = false
      this.currentImageIndex = 0
    },
  },
}
</script>

<style scoped>
.flex-center-vertically {
  display: flex;
  justify-content: center;
  flex-direction: column;
}

/* Optional styling for the preview items */
.preview-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 10px;
}

.preview-item {
  position: relative;
  width: 100%;
  height: 150px;
}

.preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 1px solid #ccc;
  border-radius: 4px;
  object-fit: contain;
  cursor: pointer;
}

.preview-one {
  height: 85vh;
  object-fit: contain;
}

.remove-button {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  border: none;
}

.remaining-count {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1.5rem;
}

.image-modal .modal-content {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 80vh !important; /* Fixed height */
}

.image-modal img {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.prev-button,
.next-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 33px;
  height: 33px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

svg {
  flex-shrink: 0;
}

.prev-button {
  left: 0;
}

.next-button {
  right: 0;
}
</style>
